import { values } from 'ramda'
import { toArray } from '../util/operators'

/**
 * The following functions used a public, MIT-licensed library as a reference
 * point:
 * @see https://github.com/mixmaxhq/search-string/blob/master/LICENSE
 * The MIT License (MIT) - Copyright (c) 2017 Mixmax <marcus@mixmax.com>
 */

// tslint:disable:no-shadowed-variable

interface QuotePositionTruthTable {
  single: Record<number, boolean>
  double: Record<number, boolean>
}

// For single and double quotes in a given string, return a truth map of each
// character position to whether is it a quote or not.
function getQuotePositionTruthTable(str = ''): QuotePositionTruthTable {
  const single: { [pos: number]: boolean } = {}
  const double: { [pos: number]: boolean } = {}

  // Track last seen quote position
  const prevQuote = { single: -1, double: -1 }
  let prevChar = ''
  for (let i = 0; i < str.length; i++) {
    const char = str[i]
    // Ignore if preceded by an escape character
    if (prevChar !== '\\') {
      if (char === '"') {
        if (prevQuote.double >= 0) {
          // Found a pair of quotes, mark their positions true
          double[prevQuote.double] = true
          double[i] = true
          prevQuote.double = -1 // Reset
        } else {
          // Track start of a quote pair
          prevQuote.double = i
        }
      } else if (char === "'") {
        if (prevQuote.single >= 0) {
          // Found a pair of quotes, mark their positions true
          single[prevQuote.single] = true
          single[i] = true
          prevQuote.single = -1 // Reset
        } else {
          // Track start of a quote pair
          prevQuote.single = i
        }
      }
    }
    prevChar = char
  }

  return { single, double }
}

/**
 * Part of filter text that is matched against all data fields.
 *
 * Ex: Filter 'foo' checks if data has any properties containing 'foo'.
 */
interface DataFilterSimpleTerm {
  text: string
  negated: boolean
}

/**
 * Part of filter text that is matched against a single data field and
 * can support multiple values.
 *
 * Ex: Filter 'myProp:foo,bar' checks if data has a property 'myProp'
 * containing 'foo' or 'bar'.
 */
export interface DataFilterFieldTerm {
  key: string
  values: string[]
  negated: boolean
}

export type DataFilterTerm = DataFilterSimpleTerm | DataFilterFieldTerm

export const isDataFilterSimpleTerm = (item: any): item is DataFilterSimpleTerm => {
  return item.text != null
}

/**
 * Parses a filter text string, returning a list of the `FilterTerm`s it
 * represents.
 *
 * By default, text is separated by whitespace into terms, e.g. `flute stop`
 * would be parsed into two terms, one for 'flute', one for 'stop'. However,
 * quoting is supported, so the filter `"nice flute" stop` would be parsed into
 * two terms, one for 'nice flute', one for 'stop'.
 *
 * Optionally supports negation, where prefixing a term with `-` indicates that
 * we are filtering against, not for, the term.
 *
 * Optionally support property terms (`FilterPropertyTerm`) using the `:`
 * operator, e.g. `foo bar:stop,flute` results in two terms, one simple one for
 * 'foo', one property term for 'bar:stop,flute', which says that the data will
 * match if it has a property 'bar' containing either 'stop' or 'flute'.
 *
 * Filter is case insensitive by default.
 */
export const parseDataFilterText = (
  filterText = '',
  opts: {
    lowercase?: boolean
    ignoreOperators?: boolean
    ignoreNegation?: boolean
  } = {
    lowercase: true,
    ignoreOperators: false,
    ignoreNegation: false,
  },
): DataFilterTerm[] => {
  const texts: DataFilterSimpleTerm[] = []
  const conditions: Record<string, DataFilterFieldTerm> = {}

  const addCondition = (key: string, value: string, negated: boolean) => {
    const prevValues = toArray(conditions[key] && conditions[key].values)
    const nextValues = [...prevValues, value]
    conditions[key] = { key, values: nextValues, negated }
  }

  const addText = (text: string, negated: boolean) => {
    texts.push({ text, negated })
  }

  let state: 'NONE' | 'IN_TEXT' | 'IN_OPERAND' = 'NONE'
  let quoteState: 'NONE' | 'SINGLE' | 'DOUBLE' = 'NONE'
  let text = ''
  let operand = ''
  let negated = false
  let prevChar = ''

  const reset = () => {
    state = 'NONE'
    quoteState = 'NONE'
    text = ''
    operand = ''
    negated = false
    prevChar = ''
  }

  const inOperand = () => !opts.ignoreOperators && state === 'IN_OPERAND'
  const inQuote = () => quoteState === 'SINGLE' || quoteState === 'DOUBLE'

  const quotePos = getQuotePositionTruthTable(filterText)

  // tslint:disable-next-line:prefer-for-of
  for (let i = 0; i < filterText.length; i++) {
    const char = filterText[i]
    if (char === ' ') {
      if (inOperand()) {
        if (inQuote()) {
          operand += char
        } else {
          addCondition(text, operand, negated)
          reset()
        }
      }
      // Space indicates end of term (unless in a quote)
      if (state === 'IN_TEXT') {
        if (inQuote()) {
          text += char
        } else {
          // End of a term reached
          addText(text, negated)
          reset()
        }
      }
    } else if (char === ',' && inOperand() && !inQuote()) {
      addCondition(text, operand, negated)
      // Don't reset, still evaluating operands for the same operator
      operand = ''
    } else if (
      !opts.ignoreNegation &&
      char === '-' &&
      state !== 'IN_OPERAND' &&
      state !== 'IN_TEXT'
    ) {
      negated = true
    } else if (char === ':' && !inQuote()) {
      if (inOperand()) {
        operand += char
      } else if (state === 'IN_TEXT') {
        // Entering operand part of segment (text after colon)
        state = 'IN_OPERAND'
      }
    } else if (char === '"' && prevChar !== '\\' && quoteState !== 'SINGLE') {
      // Handle double quote (unless was escaped or in a single quote)
      if (quoteState === 'DOUBLE') {
        // End of the double quote, reset quote state
        quoteState = 'NONE'
      } else if (quotePos.double[i]) {
        // Valid start of a double quote
        quoteState = 'DOUBLE'
      } else if (inOperand()) {
        operand += char
      } else {
        text += char
      }
    } else if (char === "'" && prevChar !== '\\' && quoteState !== 'DOUBLE') {
      // Handle single quote (unless was escaped or in a double quote)
      if (quoteState === 'SINGLE') {
        // End of the single quote, reset quote state
        quoteState = 'NONE'
      } else if (quotePos.single[i]) {
        // Valid start of a single quote
        quoteState = 'SINGLE'
      } else if (inOperand()) {
        operand += char
      } else {
        text += char
      }
    } else if (char !== '\\') {
      if (inOperand()) {
        operand += char
      } else {
        text += char
        state = 'IN_TEXT'
      }
    }
    prevChar = char
  }

  // End of the string reached: add any final terms
  if (state === 'IN_TEXT') {
    addText(text, negated)
  } else if (inOperand()) {
    addCondition(text, operand, negated)
  }

  const _terms = opts.lowercase ? texts.map((t) => ({ ...t, text: t.text.toLowerCase() })) : texts

  const _conditions = values(conditions)

  return [..._terms, ..._conditions]
}

const getDataFilterFieldTerm = (
  key: string,
  terms: DataFilterTerm[],
): DataFilterFieldTerm | undefined =>
  terms.find((term) => !isDataFilterSimpleTerm(term) && term.key === key) as
    | DataFilterFieldTerm
    | undefined

const hasDataFilterFieldTerm = (key: string, terms: DataFilterTerm[]): boolean =>
  terms.some((term) => !isDataFilterSimpleTerm(term) && key === term.key)

const removeDataFilterFieldTerm = (
  keyToRemove: string,
  terms: DataFilterTerm[],
): DataFilterTerm[] =>
  terms.filter((term) => isDataFilterSimpleTerm(term) || keyToRemove !== term.key)

const addDataFilterFieldTerm = (
  keyToAdd: string,
  valueOrValues: string | string[],
  negated: boolean,
  terms: DataFilterTerm[],
): DataFilterTerm[] => [...terms, { key: keyToAdd, values: toArray(valueOrValues), negated }]

const updateDataFilterFieldTerm = (
  keyToSet: string,
  valueOrValues: string | string[],
  negated: boolean,
  terms: DataFilterTerm[],
): DataFilterTerm[] =>
  terms.map((term) =>
    !isDataFilterSimpleTerm(term) && term.key === keyToSet
      ? { key: keyToSet, values: toArray(valueOrValues), negated }
      : term,
  )

const upsertDataFilterFieldTerm = (
  keyToSetOrAdd: string,
  valueOrValues: string | string[],
  negated: boolean,
  terms: DataFilterTerm[],
): DataFilterTerm[] =>
  hasDataFilterFieldTerm(keyToSetOrAdd, terms)
    ? updateDataFilterFieldTerm(keyToSetOrAdd, valueOrValues, negated, terms)
    : addDataFilterFieldTerm(keyToSetOrAdd, valueOrValues, negated, terms)

const toString = (terms: DataFilterTerm[]): string => {
  const init: {
    textSegments: string[]
    conditionsMap: Record<string, string[]>
  } = { textSegments: [], conditionsMap: {} }

  const { textSegments, conditionsMap } = terms.reduce((acc, term) => {
    const negatedText = term.negated ? '-' : ''
    if (isDataFilterSimpleTerm(term)) {
      acc.textSegments.push(`${negatedText}${term.text}`)
    } else {
      const key = `${negatedText}${term.key}`
      acc.conditionsMap[key] = [...toArray(acc.conditionsMap[key]), ...term.values]
    }
    return acc
  }, init)

  const conditionSegments = Object.keys(conditionsMap)
    .map((key) => {
      const values = conditionsMap[key]
      const quotedValues = values.filter((v) => v).map(quoteValue)
      if (quotedValues.length > 0) {
        return `${key}:${quotedValues.join(',')}`
      }
    })
    .filter((segment) => segment != null) as string[]

  return [...textSegments, ...conditionSegments].join(' ')
}

const quoteValue = (v: string) => {
  let quotedValue = ''
  let shouldQuote = false
  for (const char of v) {
    if (char === '"') {
      quotedValue += '\\"'
    } else {
      if (char === ' ' || char === ',') {
        shouldQuote = true
      }
      quotedValue += char
    }
  }
  return shouldQuote ? `"${quotedValue}"` : quotedValue
}
